import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { api } from '../../services/api';
import { RefreshCw, Heart, Sparkles, Loader2, Mail } from 'lucide-react';

const TURRIS_LOGO = '/logo-turris.svg';

// Confetti component
const Confetti = () => {
  const colors = ['#D32F2F', '#F4A261', '#2A9D8F', '#FFD700', '#FF69B4'];
  const pieces = Array.from({ length: 50 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 8 + Math.random() * 8
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {pieces.map((piece) => (
        <motion.div
          key={piece.id}
          className="absolute rounded-sm"
          style={{
            left: `${piece.left}%`,
            top: -20,
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.color
          }}
          initial={{ y: -20, rotate: 0, opacity: 1 }}
          animate={{ 
            y: window.innerHeight + 100, 
            rotate: 720,
            opacity: 0
          }}
          transition={{
            duration: piece.duration,
            delay: piece.delay,
            ease: "linear"
          }}
        />
      ))}
    </div>
  );
};

export default function SuccessScreen() {
  const { uiTexts, reset, protagonistName, protagonistType, userEmail } = useStory();
  const t = uiTexts || {};
  const [isSending, setIsSending] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState(false);
  const hasGeneratedRef = React.useRef(false);

  // Generate PDF and send email automatically when component mounts
  useEffect(() => {
    // Prevent double execution in React 18 StrictMode
    if (hasGeneratedRef.current) return;
    hasGeneratedRef.current = true;
    
    api.trackEvent('success_viewed');
    
    const generateAndSendPdf = async () => {
      if (!protagonistName) {
        setError(true);
        setIsSending(false);
        return;
      }
      
      try {
        // Generate booklet PDF and send email
        await api.generateBookletPdf(
          protagonistName, 
          protagonistType || 'noi',
          userEmail || null
        );
        
        setEmailSent(true);
        console.log('PDF generated and email sent to:', userEmail);
      } catch (err) {
        console.error('Error generating PDF:', err);
        setError(true);
      } finally {
        setIsSending(false);
      }
    };
    
    generateAndSendPdf();
  }, [protagonistName, protagonistType, userEmail]);

  const handleRestart = () => {
    api.trackEvent('restart_clicked');
    reset();
  };

  const handleRetry = async () => {
    setError(false);
    setIsSending(true);
    setEmailSent(false);
    
    try {
      await api.generateBookletPdf(
        protagonistName, 
        protagonistType || 'noi',
        userEmail || null
      );
      setEmailSent(true);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FDFBF7' }}>
      <Confetti />
      
      {/* Header */}
      <header className="p-6 flex justify-center">
        <motion.img
          src={TURRIS_LOGO}
          alt="Turris"
          className="h-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        />
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        {/* Success icon */}
        <motion.div
          className="relative mb-8"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
        >
          <div 
            className="w-24 h-24 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#2A9D8F' }}
          >
            <Heart className="w-12 h-12 text-white" fill="white" />
          </div>
          <motion.div
            className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#F4A261' }}
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles className="w-5 h-5 text-white" />
          </motion.div>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-4"
          style={{ color: '#2B2B2B' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {t.success_title || 'El teu conte ja està en camí!'}
        </motion.h1>

        {/* Status message */}
        {isSending ? (
          <motion.div
            className="flex items-center gap-3 text-base sm:text-lg text-center mb-8"
            style={{ color: '#5C5C5C' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#D32F2F' }} />
            Enviant el conte al teu email...
          </motion.div>
        ) : error ? (
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-base sm:text-lg mb-4" style={{ color: '#D32F2F' }}>
              Hi ha hagut un error enviant el conte.
            </p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 rounded-full text-sm font-medium text-white flex items-center gap-2 mx-auto"
              style={{ backgroundColor: '#D32F2F' }}
            >
              <RefreshCw className="w-4 h-4" />
              Tornar a intentar
            </button>
          </motion.div>
        ) : (
          <motion.div
            className="flex items-center gap-3 text-base sm:text-lg text-center mb-8 px-6 py-3 rounded-full"
            style={{ backgroundColor: '#E8F5E9', color: '#2A9D8F' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Mail className="w-5 h-5" />
            Conte enviat a {userEmail}
          </motion.div>
        )}

        {/* Restart button */}
        <motion.button
          data-testid="restart-button"
          onClick={handleRestart}
          className="px-8 py-3 rounded-full text-base font-medium flex items-center gap-2 transition-all duration-300 hover:-translate-y-1"
          style={{ 
            backgroundColor: '#F6F0E4',
            color: '#2B2B2B'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <RefreshCw className="w-4 h-4" />
          {t.success_restart || 'Crear un altre conte'}
        </motion.button>

        {/* Emotional message */}
        <motion.div
          className="mt-12 p-6 rounded-2xl max-w-md text-center"
          style={{ backgroundColor: '#F6F0E4' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <p className="text-base" style={{ color: '#2B2B2B' }}>
            {t.success_message || 'Bon Sant Jordi! Que aquesta història ompli de màgia i alegria a qui la rebi.'}
          </p>
          <p className="mt-3 font-heading text-lg font-semibold" style={{ color: '#D32F2F' }}>
            🌹 {protagonistName} 📖
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-sm" style={{ color: '#5C5C5C' }}>
          Una experiència de <span className="font-semibold">Turris</span>
        </p>
        <p className="text-xs mt-2" style={{ color: '#B0B0B0' }}>
          El Pa de Sant Jordi
        </p>
      </footer>
    </div>
  );
}
